import {
  faChevronDown,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { addDays, addMonths, addYears, format } from "date-fns"
import { de } from "date-fns/locale/de"
import { useState } from "react"
import type { ComponentProps, ReactNode } from "react"
import { cn } from "cn"

import { useIsMobile } from "../../../hooks/use-mobile"
import { Button } from "../button/button"
import { Calendar } from "../calendar/calendar"
import { DateButton } from "../date-button/date-button"
import { Popover, PopoverContent, PopoverTrigger } from "../popover/popover"

const DEFAULT_TODAY_LABEL = "Heute"

export type DateNavigatorGranularity = "day" | "month" | "year"

const STEP = {
  day: (date: Date, amount: number) => addDays(date, amount),
  month: (date: Date, amount: number) => addMonths(date, amount),
  year: (date: Date, amount: number) => addYears(date, amount),
} satisfies Record<DateNavigatorGranularity, (d: Date, n: number) => Date>

// Figma: „Mi., 20. Dezember" / „Dezember 2024" / „2026" — date-fns' de-Locale liefert „EEE" bereits mit Punkt.
const LABEL_FORMAT = {
  day: "EEE, d. MMMM",
  month: "MMMM yyyy",
  year: "yyyy",
} satisfies Record<DateNavigatorGranularity, string>

export type DateNavigatorProps = Omit<
  ComponentProps<"div">,
  "onChange" | "children"
> & {
  value: Date
  onChange: (date: Date) => void
  /**
   * Schrittweite der Pfeile, Format des Labels und Ebene des Kalenders
   * (`Calendar`-`variant`). Standard `day`.
   */
  granularity?: DateNavigatorGranularity
  /** Für Einsatz auf grauem Hintergrund (Figma-States „hover on grey"). */
  onGrey?: boolean
  /** Beschriftung des „Heute"-Buttons (Navigator und Kalender). */
  todayLabel?: string
  /** Überschreibt das automatisch formatierte Label. */
  formatLabel?: (date: Date) => ReactNode
}

/**
 * Figma „Date Navigator" (node 9777:66424) + Beispiel „Date Button" (node
 * 9754:31256): „Heute"-Button, Vor/Zurück-Pfeile und ein `DateButton`, der den
 * `Calendar` als Popover öffnet — z. B. für Listen-/Seiten-Header.
 *
 * Auf Mobile ersetzt Figma den Navigator durch die „Pagination Bar - Mobile"
 * (node 3932:9833): Pfeile außen, Label mit Chevron mittig, kein „Heute"-Button.
 * `Popover` öffnet dort automatisch als Drawer („Drawer Date Picker").
 */
function DateNavigator({
  className,
  value,
  onChange,
  granularity = "day",
  onGrey = false,
  formatLabel,
  todayLabel = DEFAULT_TODAY_LABEL,
  ...props
}: DateNavigatorProps) {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)
  const [calendarMonth, setCalendarMonth] = useState(value)

  const label = formatLabel
    ? formatLabel(value)
    : format(value, LABEL_FORMAT[granularity], { locale: de })

  function select(date: Date) {
    onChange(date)
    setOpen(false)
  }

  const prevButton = (
    <Button
      variant={onGrey ? "ghost-on-grey" : "ghost"}
      iconOnly
      aria-label="Zurück"
      onClick={() => onChange(STEP[granularity](value, -1))}
    >
      <FontAwesomeIcon icon={faChevronLeft} />
    </Button>
  )
  const nextButton = (
    <Button
      variant={onGrey ? "ghost-on-grey" : "ghost"}
      iconOnly
      aria-label="Weiter"
      onClick={() => onChange(STEP[granularity](value, 1))}
    >
      <FontAwesomeIcon icon={faChevronRight} />
    </Button>
  )

  const calendarPopover = (trigger: ReactNode) => (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        // Beim Öffnen auf den aktuellen Wert springen — `Calendar` kennt kein `defaultMonth`.
        if (next) {
          setCalendarMonth(value)
        }
      }}
    >
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-full border-none bg-transparent p-0 shadow-none md:w-fit"
      >
        <Calendar
          mode="single"
          variant={granularity}
          todayLabel={todayLabel}
          selected={value}
          month={calendarMonth}
          onMonthChange={setCalendarMonth}
          onSelect={(date) => date && select(date)}
          onMonthSelect={select}
          onYearSelect={(year) => select(new Date(year, value.getMonth(), 1))}
          className="w-full md:w-fit"
        />
      </PopoverContent>
    </Popover>
  )

  if (isMobile) {
    return (
      <div
        data-slot="date-navigator"
        className={cn(
          "flex w-full items-center justify-between border-b border-neutrals-v2-300 bg-backgrounds-primary px-4 py-2",
          className
        )}
        {...props}
      >
        {prevButton}
        {calendarPopover(
          <button
            type="button"
            data-slot="date-navigator-trigger"
            className="inline-flex cursor-pointer items-center justify-center gap-1 rounded-md px-4 py-2 text-sm leading-5 font-semibold text-labels-primary outline-none focus-visible:ring-2 focus-visible:ring-neutrals-v2-600"
          >
            {label}
            <FontAwesomeIcon icon={faChevronDown} className="size-3.5" />
          </button>
        )}
        {nextButton}
      </div>
    )
  }

  return (
    <div
      data-slot="date-navigator"
      className={cn("flex items-center gap-2", className)}
      {...props}
    >
      <Button
        variant={onGrey ? "outline-on-grey" : "outline"}
        onClick={() => onChange(new Date())}
      >
        {todayLabel}
      </Button>
      <div className="flex items-center gap-1">
        {prevButton}
        {nextButton}
        {calendarPopover(
          <DateButton data-slot="date-navigator-trigger" onGrey={onGrey}>
            {label}
          </DateButton>
        )}
      </div>
    </div>
  )
}
DateNavigator.displayName = "DateNavigator"

export { DateNavigator }
